import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Palette, Sun, Moon, Check } from 'lucide-react';
import { useTheme, themeConfigs, type ColorTheme } from '@kss/theme';
import { cn } from '../utils/cn';

const PANEL_W = 240; // matches w-60
const PANEL_GAP = 8;
const PANEL_MAX_H = 480; // tall enough for mode row + 12-swatch accent grid

export function ThemePicker({ className }: { className?: string }) {
  const { mode, colorTheme, setMode, setColorTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; maxH: number }>({
    top: 0,
    left: 0,
    maxH: PANEL_MAX_H,
  });
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  /* Compute viewport-anchored position on open. Previously the picker
     used `absolute bottom-full right-0` blindly — when the trigger sat
     at the top-left of a short page, the popup ended up at
     (top:-384, left:-188), well off-screen. Now we measure available
     space above/below the button and pick the side that fits, then
     clamp horizontally so the panel never overflows the viewport.
     A portal sidesteps any `overflow:hidden` on ancestor footer
     wrappers. */
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 8;
    const spaceBelow = vh - r.bottom - margin;
    const spaceAbove = r.top - margin;
    // Prefer below when it has room OR when there's more space below
    // than above. Either way, clamp the panel's height to the
    // available space so a tall picker scrolls internally instead of
    // bleeding past the viewport edge.
    const placement = spaceBelow >= PANEL_MAX_H || spaceBelow >= spaceAbove ? 'bottom' : 'top';
    const maxH = Math.max(
      160, // never let the panel get unusably short
      Math.min(PANEL_MAX_H, placement === 'bottom' ? spaceBelow : spaceAbove),
    );
    const top =
      placement === 'bottom'
        ? r.bottom + PANEL_GAP
        : Math.max(margin, r.top - PANEL_GAP - maxH);
    // Horizontal: anchor to the button's right edge, clamp into the
    // viewport so narrow phones don't push the panel off-screen.
    let left = r.right - PANEL_W;
    if (left < margin) left = margin;
    if (left + PANEL_W > vw - margin) left = vw - PANEL_W - margin;
    setPos({ top, left, maxH });
  }, [open]);

  // Click outside / Escape closes — checks both the trigger and the
  // portaled panel so picking a swatch doesn't dismiss before the
  // click handler fires.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={cn('relative inline-block', className)}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Theme"
        aria-expanded={open}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card-hover)]"
      >
        <Palette className="h-4 w-4" />
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Theme picker"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: PANEL_W, maxHeight: pos.maxH }}
          className="z-[60] overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3 shadow-xl"
        >
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
            Mode
          </div>
          <div className="mb-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode('light')}
              className={cn(
                'flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-medium transition-colors',
                mode === 'light'
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                  : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)]'
              )}
            >
              <Sun className="h-3.5 w-3.5" /> Light
            </button>
            <button
              type="button"
              onClick={() => setMode('dark')}
              className={cn(
                'flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-medium transition-colors',
                mode === 'dark'
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                  : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)]'
              )}
            >
              <Moon className="h-3.5 w-3.5" /> Dark
            </button>
          </div>

          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
            Accent
          </div>
          <div className="grid grid-cols-3 gap-2">
            {themeConfigs.map((t) => {
              const active = colorTheme === t.name;
              return (
                <button
                  key={t.name}
                  type="button"
                  onClick={() => setColorTheme(t.name as ColorTheme)}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-md border p-2 text-[11px] font-medium transition-colors',
                    active
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                      : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)]'
                  )}
                >
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded-full"
                    style={{ backgroundColor: t.previewColor }}
                  >
                    {active && <Check className="h-3.5 w-3.5 text-white" />}
                  </span>
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default ThemePicker;
